import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Download, ZoomIn, ZoomOut, RotateCw, FileText } from 'lucide-react';
import { getImageUrl, isImageFile, isPdfFile, transformR2PathToPublicUrl } from '@/utils/r2-url-transformer';
import { Document } from '../types';

interface DocumentPreviewDialogProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentPreviewDialog({ document, isOpen, onClose }: DocumentPreviewDialogProps) {
  const [imageError, setImageError] = React.useState(false);
  const [zoom, setZoom] = React.useState(100);
  const [rotation, setRotation] = React.useState(0);

  const resetViewState = () => {
    setZoom(100);
    setRotation(0);
    setImageError(false);
  };

  React.useEffect(() => {
    if (isOpen) {
      resetViewState();
    }
  }, [isOpen, document]);

  if (!document) return null;

  const isImage = isImageFile(document.media.storage_path) || isImageFile(document.media.file_type);
  const isPdf = isPdfFile(document.media.storage_path) || isPdfFile(document.media.file_type);
  
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  const handleDownload = () => {
    try {
      const downloadUrl = transformR2PathToPublicUrl(document.media.storage_path);
      if (downloadUrl) {
        const link = window.document.createElement('a');
        link.href = downloadUrl;
        link.download = `${document.title}.${document.media.file_type.split('/')[1] || 'file'}`;
        link.target = '_blank';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const renderPreviewContent = () => {
    if (isImage && !imageError) {
      const imageUrl = getImageUrl(document.media.thumbnail_path, document.media.storage_path);
      
      if (imageUrl) {
        return (
          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 min-h-[400px]">
            <div className="relative overflow-auto max-h-[60vh] max-w-full border rounded-lg bg-white shadow-sm">
              <img
                src={imageUrl}
                alt={document.title}
                className="block max-w-none"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                  transformOrigin: 'center',
                  transition: 'transform 0.2s ease-in-out'
                }}
                onError={() => setImageError(true)}
                onLoad={() => setImageError(false)}
              />
            </div>
            
            {/* Image Controls */}
            <div className="flex items-center gap-2 mt-4 p-2 bg-white rounded-lg shadow-sm border">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= 25}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              
              <span className="text-sm font-medium px-2">{zoom}%</span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= 300}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              
              <div className="w-px h-6 bg-border mx-2" />
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRotate}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              
              <div className="w-px h-6 bg-border mx-2" />
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
          </div>
        );
      }
    }

    if (isPdf) {
      const pdfUrl = transformR2PathToPublicUrl(document.media.storage_path);
      
      if (pdfUrl) {
        return (
          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 min-h-[400px]">
            <div className="w-full max-w-4xl bg-white rounded-lg shadow-sm border overflow-hidden">
              <iframe
                src={`${pdfUrl}#view=FitH`}
                className="w-full h-[60vh] border-0"
                title={`Preview of ${document.title}`}
              />
            </div>
            
            <div className="flex items-center gap-2 mt-4 p-2 bg-white rounded-lg shadow-sm border">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4 mr-1" />
                Download PDF
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(pdfUrl, '_blank')}
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        );
      }
    }

    // Fallback for unsupported file types or errors
    return (
      <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-8 min-h-[400px] text-center">
        <div className="flex flex-col items-center gap-4">
          {imageError ? (
            <AlertCircle className="h-12 w-12 text-amber-500" />
          ) : (
            <FileText className="h-12 w-12 text-gray-400" />
          )}
          
          <div className="space-y-2">
            <h3 className="font-medium text-gray-900">
              {imageError ? 'Preview Unavailable' : 'Preview Not Supported'}
            </h3>
            <p className="text-sm text-gray-500 max-w-md">
              {imageError 
                ? 'Unable to load the image preview. You can still download the file to view it.'
                : `Preview is not available for ${document.media.file_type} files. You can download the file to view it.`
              }
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={handleDownload}
            className="mt-2"
          >
            <Download className="h-4 w-4 mr-2" />
            Download File
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="truncate">{document.title}</span>
            <Badge variant="outline" className="ml-auto">
              {document.media.file_type}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {document.description || 'Document preview'}
            {document.media.file_size && (
              <span className="ml-2 text-xs">
                • {(document.media.file_size / 1024 / 1024).toFixed(2)} MB
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto">
          {renderPreviewContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
